import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApiService } from '../services/useApiService';
import LoadingSpinner from '../components/LoadingSpinner';
import NaibLogo from '../components/NaibLogo';
import { mapPublicAuthError } from '../utils/authErrorMessage';

function ResendVerification() {
  const location = useLocation();
  const { onboarding } = useApiService();
  const [email, setEmail] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('email') || '';
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onboarding.resendVerification(email);
      setSuccess(true);
    } catch (err) {
      const apiMessage = err.response?.data?.error;
      if (apiMessage === 'Email is already verified') {
        setError('This email is already verified. You can sign in.');
      } else {
        setError(mapPublicAuthError(err, { actionLabel: 'verification requests' }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <NaibLogo logoClassName="h-9 w-9" nameClassName="h-7 w-auto max-w-[180px]" alt="Onahiri" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Resend Verification Email
          </h2>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {success ? (
          <div className="rounded-md bg-green-50 p-4">
            <div className="text-sm text-green-700">
              If an account exists, a verification email will be sent.
              Please check your inbox.
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-indigo-600 hover:text-indigo-500"
              >
                Return to login
              </Link>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
              >
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResendVerification;
